'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Dialog, DialogBackdrop, DialogTitle } from '@headlessui/react';
import Image from 'next/image';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { useAsyncSafeState, useMounted } from '@hooks';
import { ListingBySlugPartial } from '@utils/imperative-node-queries';
import { routes } from '@routes';

// UI
import { FacebookShareButton, FacebookIcon, TwitterIcon, TwitterShareButton, EmailShareButton, EmailIcon, WhatsappIcon, WhatsappShareButton } from 'react-share';
import { Icon, IconNames } from '@components/icon';
import { ButtonIcon } from '@components/button-icon';
import { concatenateAndReplace } from '@utils/test-helpers';
type Props = {
  isOpen: boolean;
  closeModal: VoidFunction;
  listing: ListingBySlugPartial;
};
export const ShareModal: React.FC<Props> = t0 => {
  const $ = _c(70);
  const {
    isOpen,
    closeModal,
    listing
  } = t0;
  const mounted = useMounted();
  const focusDiv = React.useRef(null);
  const [showCopySuccess, setShowCopySuccess] = useAsyncSafeState(false);
  let t1;
  if ($[0] !== listing.slug) {
    t1 = listing.slug ? new URL(routes.listings({
      slug: listing.slug
    }), "https://www.savvy.com").toString() : "";
    $[0] = listing.slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pageUrl = t1;
  let t2;
  if ($[2] !== pageUrl || $[3] !== setShowCopySuccess) {
    t2 = () => {
      navigator.clipboard.writeText(pageUrl).then(() => {
        setShowCopySuccess(true);
        setTimeout(() => {
          setShowCopySuccess(false);
        }, 1500);
      });
    };
    $[2] = pageUrl;
    $[3] = setShowCopySuccess;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const copyUrl = t2;
  let t3;
  if ($[5] !== closeModal) {
    t3 = event => {
      if (event.key === "Escape") {
        closeModal();
      }
    };
    $[5] = closeModal;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleEscapeKeyPress = t3;
  let t4;
  if ($[7] !== handleEscapeKeyPress || $[8] !== isOpen) {
    t4 = () => {
      if (isOpen) {
        document.body.classList.add("modalActive");
        document.addEventListener("keyup", handleEscapeKeyPress);
      } else {
        document.body.classList.remove("modalActive");
        document.removeEventListener("keyup", handleEscapeKeyPress);
      }
    };
    $[7] = handleEscapeKeyPress;
    $[8] = isOpen;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isOpen) {
    t5 = [isOpen];
    $[10] = isOpen;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  React.useEffect(t4, t5);
  if (!mounted) {
    return null;
  }
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DialogBackdrop className="fixed inset-0 bg-primary-main/60" />;
    t7 = <span className="inline-block h-screen align-middle" aria-hidden="true">​</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[14] !== closeModal) {
    t8 = <div className="flex flex-row justify-end -mr-2 -mt-1"><ButtonIcon onClick={closeModal} variant="text" icon={IconNames.X} ariaLabel="Close" ariaDescribedby="close-share-modal" /></div>;
    $[14] = closeModal;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogTitle as="h2" className="text-xl font-semibold text-primary-main text-center mt-1">Share this listing</DialogTitle>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== listing.galleryAssets[0] || $[18] !== listing.galleryAssets.length) {
    t10 = listing.galleryAssets.length > 0 && <div className="w-full h-48 mx-auto relative rounded-xl overflow-hidden"><Image fill={true} className="object-cover object-center rounded-lg" src={listing.galleryAssets[0].url || ""} alt={listing.galleryAssets[0].description || "listing image"} placeholder="blur" loader={imagekitLoader} blurDataURL={GRAY_PIXEL_DATA_URL} sizes="28rem" /></div>;
    $[17] = listing.galleryAssets[0];
    $[18] = listing.galleryAssets.length;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = `${listing.name}`;
  let t12;
  if ($[20] !== t11) {
    t12 = concatenateAndReplace(t11);
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== listing.name || $[23] !== t12) {
    t13 = <h3 className="text-base font-semibold text-primary-main text-center" data-testid={t12}>{listing.name}</h3>;
    $[22] = listing.name;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t10 || $[26] !== t13) {
    t14 = <div className="flex flex-col w-full mt-5 space-y-4">{t10}{t13}</div>;
    $[25] = t10;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <FacebookIcon className="w-11 h-11 rounded-xl" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== pageUrl) {
    t16 = <FacebookShareButton className="outline-none focus-visible:ring-2 focus-visible:ring-primary-main rounded-xl" url={pageUrl} hashtag="Savvy">{t15}</FacebookShareButton>;
    $[29] = pageUrl;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  let t18;
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = ["Savvy"];
    t18 = ["Savvy"];
    t19 = <TwitterIcon className="w-11 h-11 rounded-xl" />;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t17 = $[31];
    t18 = $[32];
    t19 = $[33];
  }
  let t20;
  if ($[34] !== pageUrl) {
    t20 = <TwitterShareButton className="outline-none focus-visible:ring-2 focus-visible:ring-primary-main rounded-xl" url={pageUrl} title="test" hashtags={t17} related={t18}>{t19}</TwitterShareButton>;
    $[34] = pageUrl;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const t21 = `Check out this vacation rental on Savvy! ${pageUrl}`;
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <EmailIcon className="w-11 h-11 rounded-xl" />;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== pageUrl || $[38] !== t21) {
    t23 = <EmailShareButton className="outline-none focus-visible:ring-2 focus-visible:ring-primary-main rounded-xl" url={pageUrl} subject="test" body={t21}>{t22}</EmailShareButton>;
    $[37] = pageUrl;
    $[38] = t21;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  const t24 = `sms:&body=Check out this vacation rental on Savvy! ${pageUrl}`;
  let t25;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" focusable="false" className="w-11 h-11 rounded-xl"><defs><linearGradient x1="50.794%" y1="93.362%" x2="50.794%" y2="12.41%" id="imessagegradient"><stop stopColor="#0CBD2A" offset="0%" /><stop stopColor="#5BF675" offset="100%" /></linearGradient></defs><g><path d="M2 0h28a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z" fill="url(#imessagegradient)" /><path d="M15.796 5.469c-6.404 0-11.595 4.324-11.595 9.658.005 3.39 2.143 6.528 5.633 8.27-.457 1.023-1.142 1.983-2.028 2.838 1.717-.3 3.329-.934 4.71-1.85 1.064.264 2.17.399 3.28.4 6.404 0 11.596-4.324 11.596-9.658S22.2 5.47 15.796 5.47z" fill="#FFF" /></g></svg>;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t24) {
    t26 = <a className="outline-none focus-visible:ring-2 focus-visible:ring-primary-main rounded-xl" href={t24}>{t25}</a>;
    $[41] = t24;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <WhatsappIcon className="w-11 h-11 rounded-xl" />;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== pageUrl) {
    t28 = <WhatsappShareButton className="outline-none focus-visible:ring-2 focus-visible:ring-primary-main rounded-xl" url={pageUrl} title="test">{t27}</WhatsappShareButton>;
    $[44] = pageUrl;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== t16 || $[47] !== t20 || $[48] !== t23 || $[49] !== t26 || $[50] !== t28) {
    t29 = <div className="mt-6 flex flex-row justify-center gap-3">{t16}{t20}{t23}{t26}{t28}</div>;
    $[46] = t16;
    $[47] = t20;
    $[48] = t23;
    $[49] = t26;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="flex items-center pl-3 text-primary-200"><Icon name={IconNames.LINK} size="sm" /></div>;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== pageUrl) {
    t31 = <input readOnly={true} value={pageUrl} type="text" name="copy-url" aria-label="Listing URL" className="flex-1 min-w-0 bg-transparent px-2 py-3 text-sm text-primary-main outline-none truncate" />;
    $[53] = pageUrl;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  const t32 = showCopySuccess ? "Copied!" : "Copy";
  let t33;
  if ($[55] !== copyUrl || $[56] !== t32) {
    t33 = <button onClick={copyUrl} type="button" className="shrink-0 px-5 py-3 text-sm font-semibold text-white bg-primary-main hover:bg-primary-200 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white">{t32}</button>;
    $[55] = copyUrl;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== t31 || $[59] !== t33) {
    t34 = <div className="mt-6 mx-auto flex items-center gap-0 rounded-lg border-[1.5px] border-gray-400 bg-gray-100 overflow-hidden transition-colors focus-within:border-primary-main">{t30}{t31}{t33}</div>;
    $[58] = t31;
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t14 || $[62] !== t29 || $[63] !== t34 || $[64] !== t8) {
    t35 = <div ref={focusDiv} className="min-h-screen px-4 text-center">{t6}{t7}<div className="relative inline-block w-full max-w-md px-6 pt-5 pb-8 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-lg rounded-2xl">{t8}{t9}{t14}{t29}{t34}</div></div>;
    $[61] = t14;
    $[62] = t29;
    $[63] = t34;
    $[64] = t8;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== closeModal || $[67] !== isOpen || $[68] !== t35) {
    t36 = <Dialog initialFocus={focusDiv} as="div" className="fixed inset-0 overflow-y-auto z-50 font-sans" onClose={closeModal} open={isOpen}>{t35}</Dialog>;
    $[66] = closeModal;
    $[67] = isOpen;
    $[68] = t35;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  return t36;
};